import React, { useState, useEffect } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import '../../styles/dialog-premium.css';

interface AnimatedPopupProps {
  isOpen: boolean;
  onClose: () => void;
  popupName?: string;
  popupContain?: string;
  onYes?: () => void | Promise<void>;
  onNo?: () => void | Promise<void>;
  yesButtonText?: string;
  noButtonText?: string;
  yesButtonClass?: string;
  noButtonClass?: string;
}

const Popup: React.FC<AnimatedPopupProps> = ({
  isOpen,
  onClose,
  popupName = "Confirm Action",
  popupContain = "Are you sure you want to proceed?",
  onYes,
  onNo,
  yesButtonText = "Yes",
  noButtonText = "No",
  yesButtonClass = "btn-yes",
  noButtonClass = "btn-no"
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 200); // Match with animation duration
  };

  const handleYes = () => {
    if (onYes) onYes();
    handleClose();
  };

  const handleNo = () => {
    if (onNo) onNo();
    handleClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`popup-overlay ${isAnimating ? 'animate-in' : 'animate-out'}`}
      onClick={handleBackdropClick}
    >
      <div className={`popup-container ${isAnimating ? 'popup-animate-in' : 'popup-animate-out'}`}>
        <div className="popup-header">
          <h4 className="popup-title">{popupName}</h4>
          <button
            className="popup-close-btn"
            onClick={handleClose}
            type="button"
            aria-label="Close"
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="popup-body premium-dialog-body--with-icon">
          <div className="premium-dialog-icon premium-dialog-icon--info">
            <InfoOutlinedIcon />
          </div>
          <p className="popup-content">{popupContain}</p>
        </div>

        <div className="popup-footer">
          <button
            className={`popup-btn ${noButtonClass}`}
            onClick={handleNo}
            type="button"
          >
            {noButtonText}
          </button>
          <button
            className={`popup-btn ${yesButtonClass}`}
            onClick={handleYes}
            type="button"
          >
            {yesButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Popup;
